import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import axios from 'axios';
import { useState, type ReactElement } from 'react';
import { Controller, useForm, type Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { BrandPanel } from './_components/BrandPanel';
import { PasswordField } from './_components/PasswordField';
import { StepHeading } from './_components/StepHeading';
import { policyI18nKey, validatePassword, type PasswordIssue } from './_lib/passwordPolicy';
import { tokens } from '../../design/tokens';
import { Routes } from '../../routes/routes';
import { useAuthStore } from '../../stores';

/**
 * `SetPasswordPage` (AUT-110) — invite-acceptance landing.
 *
 * URL: `/auth/accept?token=<raw>`. Renders a password + confirm form
 * gated on the `?token=` query param (no token → "ссылка некорректна").
 *
 * On submit:
 *   1. Run the SPA-side policy preview (`validatePassword`) — server
 *      re-validates on POST anyway, but inline policy preview gives the
 *      operator immediate feedback before the round-trip.
 *   2. POST `/admin/invites/accept` `{token, password}` via
 *      `authStore.acceptInvite`.
 *   3. On success, the store has already issued tokens — navigate to
 *      Dashboard.
 *   4. On 422 policy violation → surface `details.reason` inline.
 *   5. On 404 invite-not-found → "ссылка устарела".
 *   6. On 409 email-collision → "email уже используется".
 */

const FormSchema = z
  .object({
    password: z.string().min(1, 'auth.invite.setPassword.policy.tooShort'),
    confirm: z.string().min(1, 'auth.invite.setPassword.policy.mismatch'),
  })
  .refine((d) => d.password === d.confirm, {
    path: ['confirm'],
    message: 'auth.invite.setPassword.policy.mismatch',
  });

type FormValues = z.infer<typeof FormSchema>;

type PolicyError = { kind: 'policy'; issue: PasswordIssue };
type ServerError = { kind: 'server'; key: string };
type PageError = PolicyError | ServerError;

const mapAcceptError = (err: unknown): PageError => {
  if (!axios.isAxiosError(err) || !err.response) {
    return { kind: 'server', key: 'auth.errors.network' };
  }
  const status = err.response.status;
  const data = err.response.data as
    | { code?: string; details?: { reason?: PasswordIssue } }
    | undefined;
  if (status === 422 && data?.code === 'admin_password_policy_violation') {
    const reason = data.details?.reason;
    if (reason) return { kind: 'policy', issue: reason };
  }
  if (status === 404 || data?.code === 'admin_invite_not_found') {
    return { kind: 'server', key: 'auth.invite.setPassword.errors.expired' };
  }
  if (status === 409 || data?.code === 'admin_email_already_exists') {
    return { kind: 'server', key: 'auth.invite.setPassword.errors.emailCollision' };
  }
  if (status >= 500) return { kind: 'server', key: 'auth.errors.server' };
  return { kind: 'server', key: 'auth.errors.unknown' };
};

type FieldsProps = {
  control: Control<FormValues>;
  disabled: boolean;
  passwordLabel: string;
  confirmLabel: string;
};

const InviteFields = ({
  control,
  disabled,
  passwordLabel,
  confirmLabel,
}: FieldsProps): ReactElement => (
  <>
    <Controller
      name="password"
      control={control}
      render={({ field, fieldState }) => (
        <PasswordField
          {...field}
          fullWidth
          autoComplete="new-password"
          label={passwordLabel}
          disabled={disabled}
          error={Boolean(fieldState.error)}
          inputProps={{ 'aria-label': passwordLabel, 'data-testid': 'set-password-input' }}
          sx={{ mb: '16px' }}
        />
      )}
    />
    <Controller
      name="confirm"
      control={control}
      render={({ field, fieldState }) => (
        <PasswordField
          {...field}
          fullWidth
          autoComplete="new-password"
          label={confirmLabel}
          disabled={disabled}
          error={Boolean(fieldState.error)}
          inputProps={{ 'aria-label': confirmLabel, 'data-testid': 'set-password-confirm-input' }}
          sx={{ mb: '16px' }}
        />
      )}
    />
  </>
);

type SetPasswordFormProps = {
  token: string;
};

const SetPasswordForm = ({ token }: SetPasswordFormProps): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState<PageError | null>(null);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { password: '', confirm: '' },
    mode: 'onSubmit',
  });

  const onSubmit = async (values: FormValues): Promise<void> => {
    setPageError(null);
    const policy = validatePassword(values.password);
    if (!policy.ok) {
      setPageError({ kind: 'policy', issue: policy.reason });
      return;
    }
    setSubmitting(true);
    try {
      await authStore.acceptInvite(token, values.password);
      navigate(Routes.App.Dashboard, { replace: true });
    } catch (err) {
      setPageError(mapAcceptError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inlineError: string | null = pageError
    ? pageError.kind === 'policy'
      ? t(policyI18nKey(pageError.issue))
      : t(pageError.key)
    : errors.confirm
      ? t('auth.invite.setPassword.policy.mismatch')
      : null;

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <StepHeading
        eyebrow={t('auth.login.step.eyebrow')}
        title={t('auth.invite.setPassword.title')}
        subtitle={t('auth.invite.setPassword.subtitle')}
      />
      <InviteFields
        control={control}
        disabled={submitting}
        passwordLabel={t('auth.invite.setPassword.passwordLabel')}
        confirmLabel={t('auth.invite.setPassword.confirmLabel')}
      />
      {inlineError && (
        <Alert severity="error" sx={{ mb: '16px' }} data-testid="set-password-error">
          {inlineError}
        </Alert>
      )}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={submitting}
        sx={{ height: 52, borderRadius: '14px' }}
        data-testid="set-password-submit"
      >
        {t('auth.invite.setPassword.cta')}
      </Button>
    </Box>
  );
};

const MissingTokenNotice = (): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box>
      <StepHeading
        eyebrow={t('auth.login.step.eyebrow')}
        title={t('auth.invite.setPassword.title')}
        subtitle={t('auth.invite.setPassword.errors.tokenMissing')}
      />
      <Alert severity="error" sx={{ mb: '16px' }} data-testid="set-password-token-invalid">
        {t('auth.invite.setPassword.errors.tokenMissing')}
      </Alert>
      <Box sx={{ textAlign: 'center' }}>
        <Link component={RouterLink} to={Routes.Auth.Login} underline="hover" sx={{ fontSize: 13 }}>
          {t('auth.invite.setPassword.errors.backToLogin')}
        </Link>
      </Box>
    </Box>
  );
};

export const SetPasswordPage = (): ReactElement => {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: tokens.surface.base }}>
      <BrandPanel step="login" email="" />
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '56px',
        }}
      >
        <Paper elevation={0} sx={{ width: '100%', maxWidth: 420, background: 'transparent' }}>
          {token ? <SetPasswordForm token={token} /> : <MissingTokenNotice />}
        </Paper>
      </Box>
    </Box>
  );
};
